/**
 * "Upload new" tab — accepts video / PPTX / PDF (filtered by allowed types
 * for the gap), drives the existing video / presentation upload endpoints,
 * polls for readiness, and surfaces the freshly-ready asset to the caller
 * for validation.
 *
 * Polling stops after a soft cap (5 min) and shows a "still processing"
 * notice — the user can dismiss the modal and try again later, the asset
 * remains in the org library.
 */
import { useEffect, useRef, useState } from 'react';
import { CloudUpload, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import contentLibraryService from '@/lib/api/contentLibrary';
import type {
  KGAssetRef,
  KGAssetType,
} from '@/lib/api/knowledgeGaps';

const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm';
const VIDEO_AND_DOC_ACCEPT = `${VIDEO_ACCEPT},.pptx,.pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf`;
const GENERIC_UPLOAD_ERROR = 'We could not upload this source. Try again or choose a different file.';

const POLL_INTERVAL_MS = 4_000;
const POLL_BACKOFF_MS = 10_000;
const POLL_BACKOFF_AFTER_MS = 60_000;
const POLL_TIMEOUT_MS = 300_000;

type UploadKind = 'video' | 'presentation_deck';

function classifyFile(file: File): UploadKind | null {
  const name = file.name.toLowerCase();
  if (
    file.type.startsWith('video/') ||
    name.endsWith('.mp4') ||
    name.endsWith('.mov') ||
    name.endsWith('.avi') ||
    name.endsWith('.webm')
  ) {
    return 'video';
  }
  if (
    name.endsWith('.pptx') ||
    name.endsWith('.pdf') ||
    file.type === 'application/pdf' ||
    file.type ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return 'presentation_deck';
  }
  return null;
}

function getCustomerUploadErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return GENERIC_UPLOAD_ERROR;

  const message = err.message.trim();
  if (
    message === 'Unsupported file type.' ||
    message.includes('Only video') ||
    message.includes('Only video, PPTX, or PDF') ||
    message.includes('still processing')
  ) {
    return message;
  }

  return GENERIC_UPLOAD_ERROR;
}

export function UploadNewTab({
  orgId,
  allowedTypes,
  onAssetReady,
  isReadyHandlerBusy,
}: {
  orgId: string;
  allowedTypes: ReadonlyArray<KGAssetType>;
  onAssetReady: (ref: KGAssetRef) => void;
  isReadyHandlerBusy: boolean;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<
    'idle' | 'uploading' | 'processing' | 'ready' | 'slow' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingRef, setPendingRef] = useState<KGAssetRef | null>(null);
  // Bumped on reset() to force-remount the file input so its underlying
  // DOM `files` collection is cleared. Without this, picking the same
  // file path again after "Choose another" yields no `change` event and
  // the Upload button never reappears. See attach-source/UploadNewTab
  // bug investigation for details.
  const [inputKey, setInputKey] = useState(0);
  // AbortController-based cancellation: each upload session owns its own
  // controller. reset() / unmount aborts it; the upload + poll helpers
  // observe `signal.aborted` and `sleep` listens for the `abort` event so
  // its timer chain has a definitive termination signal (no polling leak).
  const abortRef = useRef<AbortController | null>(null);

  const accept =
    allowedTypes.length === 1 && allowedTypes[0] === 'video'
      ? VIDEO_ACCEPT
      : VIDEO_AND_DOC_ACCEPT;

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setFile(null);
    setPhase('idle');
    setErrorMessage(null);
    setPendingRef(null);
    setInputKey((k) => k + 1);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    if (!next) return;

    const kind = classifyFile(next);
    if (!kind || !allowedTypes.includes(kind)) {
      setErrorMessage(
        allowedTypes.length === 1 && allowedTypes[0] === 'video'
          ? 'Only video uploads are supported for this gap.'
          : 'Only video, PPTX, or PDF files are supported.',
      );
      return;
    }
    setFile(next);
    setErrorMessage(null);
  };

  const startUpload = async () => {
    if (!file) return;
    const kind = classifyFile(file);
    if (!kind) {
      setErrorMessage('Unsupported file type.');
      return;
    }

    // Abort any prior in-flight upload (defensive — the start-upload trigger
    // is gated by phase === 'idle' so this should be a no-op in practice).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPhase('uploading');
    setErrorMessage(null);
    setPendingRef(null);

    try {
      if (kind === 'video') {
        const ref = await uploadVideoAndPoll({
          orgId,
          file,
          signal: controller.signal,
          onProcessing: () => setPhase('processing'),
          onSlow: () => setPhase('slow'),
        });
        if (controller.signal.aborted) return;
        setPendingRef(ref);
        setPhase('ready');
      } else {
        const ref = await uploadPresentationAndPoll({
          orgId,
          file,
          signal: controller.signal,
          onProcessing: () => setPhase('processing'),
          onSlow: () => setPhase('slow'),
        });
        if (controller.signal.aborted) return;
        setPendingRef(ref);
        setPhase('ready');
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = getCustomerUploadErrorMessage(err);
      setErrorMessage(message);
      setPhase('error');
      toast({
        title: 'Upload failed',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CloudUpload className="h-4 w-4 text-muted-foreground" />
          {allowedTypes.length === 1 && allowedTypes[0] === 'video'
            ? 'Upload a video'
            : 'Upload a video, PPTX, or PDF'}
        </div>
        <p className="text-xs text-muted-foreground">
          {allowedTypes.length === 1 && allowedTypes[0] === 'video'
            ? 'Accepted: MP4, MOV, AVI, WebM. The video must be segmented before it can be attached.'
            : 'Accepted: MP4, MOV, AVI, WebM, PPTX, PDF. The file is processed before it can be attached.'}
        </p>
        <Input
          key={inputKey}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={phase === 'uploading' || phase === 'processing' || phase === 'slow'}
        />
        {errorMessage && phase !== 'error' && (
          <p className="text-xs text-destructive">{errorMessage}</p>
        )}
      </div>

      {file && phase === 'idle' && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm truncate">{file.name}</span>
          <Button size="sm" onClick={startUpload}>
            Upload
          </Button>
        </div>
      )}

      {(phase === 'uploading' || phase === 'processing' || phase === 'slow') && (
        <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
          <div className="space-y-0.5 min-w-0 flex-1">
            <p className="font-medium truncate">{file?.name}</p>
            <p className="text-xs text-muted-foreground">
              {phase === 'uploading' && 'Uploading…'}
              {phase === 'processing' && 'Processing…'}
              {phase === 'slow' &&
                "Still processing — this is taking longer than usual. You can come back later."}
            </p>
          </div>
        </div>
      )}

      {phase === 'ready' && pendingRef && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20 p-3 text-sm">
          <div className="space-y-0.5 min-w-0 flex-1">
            <p className="font-medium">Ready to validate</p>
            <p className="text-xs text-muted-foreground truncate">
              {file?.name}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              disabled={isReadyHandlerBusy}
            >
              Choose another
            </Button>
            <Button
              size="sm"
              onClick={() => onAssetReady(pendingRef)}
              disabled={isReadyHandlerBusy}
            >
              {isReadyHandlerBusy && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              )}
              Validate
            </Button>
          </div>
        </div>
      )}

      {phase === 'error' && errorMessage && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2 text-sm">
          <p className="text-destructive">{errorMessage}</p>
          <Button variant="outline" size="sm" onClick={reset}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Upload + poll helpers ─────────────────────────────────────────────────

async function uploadVideoAndPoll({
  orgId,
  file,
  signal,
  onProcessing,
  onSlow,
}: {
  orgId: string;
  file: File;
  signal: AbortSignal;
  onProcessing: () => void;
  onSlow: () => void;
}): Promise<KGAssetRef> {
  // 3-step signed URL flow.
  const requestRes = await contentLibraryService.requestUpload({
    organization_id: orgId,
    filename: file.name,
    title: file.name,
  });
  if (!requestRes.success || !requestRes.data) {
    throw new Error(requestRes.error || 'Failed to start video upload');
  }
  const videoId = requestRes.data.video_id;

  await contentLibraryService.uploadToSignedUrl(requestRes.data.upload_url, file);
  if (signal.aborted) throw new Error('cancelled');

  const confirmRes = await contentLibraryService.confirmUpload(videoId, {
    segmentation_mode: 'auto',
  });
  if (!confirmRes.success) {
    throw new Error(confirmRes.error || 'Failed to start video processing');
  }
  if (signal.aborted) throw new Error('cancelled');

  onProcessing();

  await pollVideoUntilReady({ videoId, signal, onSlow });
  return { asset_type: 'video', asset_id: videoId };
}

async function pollVideoUntilReady({
  videoId,
  signal,
  onSlow,
}: {
  videoId: string;
  signal: AbortSignal;
  onSlow: () => void;
}): Promise<void> {
  const startedAt = Date.now();
  let slowFired = false;

  while (!signal.aborted) {
    const elapsed = Date.now() - startedAt;
    if (elapsed > POLL_TIMEOUT_MS) {
      throw new Error(
        'Video is still processing after 5 minutes. You can attach it later from the existing-sources tab.',
      );
    }

    const statusRes = await contentLibraryService.getVideoStatus(videoId);
    if (signal.aborted) return;
    if (!statusRes.success || !statusRes.data) {
      throw new Error(statusRes.error || 'Failed to poll video status');
    }
    const { status, error_message } = statusRes.data;
    if (status === 'ready') {
      return;
    }
    if (status === 'error') {
      throw new Error(
        error_message || 'Video processing failed. Please try a different file.',
      );
    }
    if (!slowFired && elapsed > POLL_BACKOFF_AFTER_MS) {
      slowFired = true;
      onSlow();
    }
    const interval = elapsed > POLL_BACKOFF_AFTER_MS ? POLL_BACKOFF_MS : POLL_INTERVAL_MS;
    await sleep(interval, signal);
  }
}

async function uploadPresentationAndPoll({
  orgId,
  file,
  signal,
  onProcessing,
  onSlow,
}: {
  orgId: string;
  file: File;
  signal: AbortSignal;
  onProcessing: () => void;
  onSlow: () => void;
}): Promise<KGAssetRef> {
  // The upload endpoint creates the deck row with total_slides=0 and
  // dispatches slide extraction as a FastAPI BackgroundTask — the response
  // returns BEFORE slides are committed. We have to poll until the deck is
  // actually ready (total_slides > 0 AND at least one slide row), otherwise
  // the validate-asset endpoint immediately rejects with `asset_not_ready`.
  const res = await contentLibraryService.uploadPresentation({
    organization_id: orgId,
    file,
    title: file.name,
  });
  if (signal.aborted) throw new Error('cancelled');
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to upload presentation');
  }
  const deckId = res.data.deck.deck_id;

  // Move from 'uploading' to 'processing' only after the file transfer
  // completes — mirrors the video flow's placement so the user sees an
  // accurate status throughout the lifecycle.
  onProcessing();
  await pollDeckUntilReady({ deckId, signal, onSlow });
  return { asset_type: 'presentation_deck', asset_id: deckId };
}

async function pollDeckUntilReady({
  deckId,
  signal,
  onSlow,
}: {
  deckId: string;
  signal: AbortSignal;
  onSlow: () => void;
}): Promise<void> {
  const startedAt = Date.now();
  let slowFired = false;

  while (!signal.aborted) {
    const elapsed = Date.now() - startedAt;
    if (elapsed > POLL_TIMEOUT_MS) {
      throw new Error(
        'Document is still processing after 5 minutes. You can attach it later from the existing-sources tab.',
      );
    }

    const statusRes = await contentLibraryService.getDeckById(deckId);
    if (signal.aborted) return;
    if (!statusRes.success || !statusRes.data?.deck) {
      throw new Error(statusRes.error || 'Failed to poll deck status');
    }

    // Mirror the backend's `_is_deck_ready` check exactly:
    //   total_slides > 0 AND at least one slide row committed.
    // total_slides is set by the background task before the slide rows are
    // inserted, so checking just one half opens a race window.
    const total = statusRes.data.deck.total_slides ?? 0;
    const slideCount = statusRes.data.slides?.length ?? 0;
    if (total > 0 && slideCount > 0) {
      return;
    }

    if (!slowFired && elapsed > POLL_BACKOFF_AFTER_MS) {
      slowFired = true;
      onSlow();
    }
    const interval =
      elapsed > POLL_BACKOFF_AFTER_MS ? POLL_BACKOFF_MS : POLL_INTERVAL_MS;
    await sleep(interval, signal);
  }
}

/**
 * AbortSignal-aware delay. Resolves after `ms` ms, OR immediately when the
 * signal aborts. The previous implementation used a 100 Hz polling chain
 * to detect cancellation — that chain leaked past resolution because the
 * two timers had no shared termination signal. Native abort eventing
 * removes the leak surface entirely.
 */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
